import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import AppShell from "../components/app/AppShell";
import { api } from "../lib/api";
import { useT } from "../contexts/LanguageContext";
import { toast } from "sonner";
import { CaretLeft, CaretRight, Translate } from "@phosphor-icons/react";

const LETTERS = ["A", "B", "C", "D"];

export default function QuizRunner() {
  const { t, tDomain, tDiff } = useT();
  const location = useLocation();
  const navigate = useNavigate();
  const initialQuiz = location.state?.quiz;
  const mode = location.state?.mode || "practice";

  const [quiz, setQuiz] = useState(initialQuiz);
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [startedAt] = useState(Date.now());

  useEffect(() => {
    if (!initialQuiz) navigate("/practice", { replace: true });
  }, [initialQuiz, navigate]);

  if (!quiz) return null;
  const total = quiz.questions.length;
  const q = quiz.questions[idx];
  const selected = answers[q.question_id];
  const progressPct = Math.round(((idx + 1) / total) * 100);
  const answered = Object.keys(answers).length;
  const currentLang = quiz.lang || "en";
  const switchTo = currentLang === "tr" ? "en" : "tr";

  const select = (i) => setAnswers({ ...answers, [q.question_id]: i });

  const switchLanguage = async () => {
    setTranslating(true);
    try {
      const { data } = await api.post("/questions/translate", {
        question_ids: quiz.questions.map((qq) => qq.question_id),
        lang: switchTo,
      });
      const byId = Object.fromEntries(data.questions.map((x) => [x.question_id, x]));
      setQuiz({
        ...quiz,
        lang: switchTo,
        questions: quiz.questions.map((qq) => ({ ...qq, ...byId[qq.question_id] })),
      });
    } catch {
      toast.error(t("common.failed"));
    } finally {
      setTranslating(false);
    }
  };

  const finish = async () => {
    setSubmitting(true);
    try {
      const payloadAnswers = quiz.questions.map((qq) => ({
        question_id: qq.question_id,
        selected_index: answers[qq.question_id] ?? null,
        is_correct: false,
        time_seconds: 0,
      }));
      const duration = Math.round((Date.now() - startedAt) / 1000);
      const { data } = await api.post("/quiz/submit", {
        attempt_id: quiz.attempt_id,
        answers: payloadAnswers,
        duration_seconds: duration,
      });
      navigate(`/results/${data.attempt_id}`, { replace: true });
    } catch (e) {
      toast.error(t("quiz.failed_submit"));
      setSubmitting(false);
    }
  };

  return (
    <AppShell>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="overline text-[#002FA7]">{mode === "mock" ? t("quiz.mock_label") : t("quiz.practice_label")}</div>
          <h1 className="font-display text-2xl font-bold tracking-tight mt-1" data-testid="quiz-progress-heading">
            {t("quiz.q_of", { n: idx + 1, total })}
          </h1>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={switchLanguage}
            disabled={translating}
            className="btn-secondary !py-2 !px-3 text-xs flex items-center gap-1.5"
            data-testid="quiz-language-toggle"
            aria-label="Toggle question language"
          >
            <Translate size={14} weight="bold" />
            <span className="font-mono font-bold">{currentLang.toUpperCase()}</span>
            <span className="text-slate-400">/</span>
            <span className="text-slate-400 font-mono">{switchTo.toUpperCase()}</span>
            {translating && <span className="text-[10px] text-slate-500">{t("quiz.translating")}</span>}
          </button>
          <div className="text-sm text-slate-500">{t("quiz.answered")} <span className="font-mono font-bold text-slate-900">{answered}/{total}</span></div>
        </div>
      </div>

      <div className="mt-3 h-1.5 w-full bg-slate-100 rounded-full overflow-hidden">
        <div className="h-full bg-[#002FA7] transition-all duration-300" style={{ width: `${progressPct}%` }} />
      </div>

      <div className="mt-8 max-w-3xl mx-auto" data-testid="quiz-question-container">
        <div className="flex items-center gap-2">
          <span className="overline">{tDomain(q.domain)}</span>
          <span className="overline text-slate-400">·</span>
          <span className="overline">{tDiff(q.difficulty)}</span>
        </div>
        <h2 className="font-display text-2xl sm:text-3xl font-semibold mt-3 leading-snug" data-testid="quiz-question-text">{q.text}</h2>

        <div className="mt-6 space-y-3">
          {q.options.map((opt, i) => (
            <button
              key={i}
              className={`option-tile ${selected === i ? "selected" : ""}`}
              onClick={() => select(i)}
              data-testid={`quiz-option-${i}`}
            >
              <span className="option-letter">{LETTERS[i]}</span>
              <span className="text-base leading-relaxed">{opt}</span>
            </button>
          ))}
        </div>

        <div className="mt-8 flex items-center justify-between gap-3">
          <button className="btn-secondary" disabled={idx === 0} onClick={() => setIdx(Math.max(0, idx - 1))} data-testid="quiz-prev-button">
            <CaretLeft size={16} /> {t("common.previous")}
          </button>
          {idx < total - 1 ? (
            <button className="btn-primary" onClick={() => setIdx(idx + 1)} data-testid="quiz-next-button">
              {t("common.next")} <CaretRight size={16} />
            </button>
          ) : (
            <button className="btn-primary" onClick={finish} disabled={submitting} data-testid="quiz-finish-button">
              {submitting ? t("quiz.submitting") : t("quiz.finish_analyze")}
            </button>
          )}
        </div>
      </div>
    </AppShell>
  );
}
